import * as React from 'react'

import {
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  MoreHorizontalIcon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

import { ICardListItem, ICardListState } from './types'

interface IProps<T extends ICardListItem> {
  listState: ICardListState<T>
  className?: string
  portalSelector?: string
  /** Selector counters (Figma select menu): 16px icons 8px apart, expand before collapse. */
  compact?: boolean
}

export function CardListGroupToggles<T extends ICardListItem>({
  listState,
  className,
  portalSelector,
  compact,
}: IProps<T>): React.ReactElement | null {
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    if (portalSelector) {
      setPortalContainer(document.querySelector<HTMLElement>(portalSelector))
    }
  }, [portalSelector])

  if (listState.groups.length === 0) return null

  const isTreeShaped = listState.groups.some(g => g.depth > 0)
  const buttonSize = compact ? 'icon-xs' : 'icon'
  const buttonClassName = compact ? 'tw:[&_svg]:!size-4' : undefined

  if (!isTreeShaped) {
    // Mixed-state click collapses every band (product convention across grouped
    // lists). Do not add confirm / tri-state without design.
    const isExpanded = listState.canCollapseMore
    return (
      <CollapseButton
        isExpanded={isExpanded}
        onClick={
          isExpanded ? listState.collapseAllGroups : listState.expandAllGroups
        }
        ariaLabel={
          isExpanded
            ? Localization.translator.collapseAll
            : Localization.translator.expandAll
        }
        size={buttonSize}
        className={cn(buttonClassName, className)}
      />
    )
  }

  const collapseOneLevel = (
    <Button
      type="button"
      variant="ghost"
      size={buttonSize}
      className={buttonClassName}
      onClick={listState.incrementalCollapse}
      disabled={!listState.canCollapseMore}
      aria-label={Localization.translator.collapseOneLevel}
      title={Localization.translator.collapseOneLevel}
    >
      <ChevronsDownUpIcon aria-hidden="true" />
    </Button>
  )
  const expandOneLevel = (
    <Button
      type="button"
      variant="ghost"
      size={buttonSize}
      className={buttonClassName}
      onClick={listState.incrementalExpand}
      disabled={!listState.canExpandMore}
      aria-label={Localization.translator.expandOneLevel}
      title={Localization.translator.expandOneLevel}
    >
      <ChevronsUpDownIcon aria-hidden="true" />
    </Button>
  )

  return (
    <div
      className={cn(
        'tw:flex tw:shrink-0 tw:items-center',
        compact ? 'tw:gap-0' : 'tw:gap-0.5',
        className,
      )}
    >
      {compact ? expandOneLevel : collapseOneLevel}
      {compact ? collapseOneLevel : expandOneLevel}
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={Localization.translator.moreOptions}
          render={
            <Button
              type="button"
              variant="ghost"
              size={buttonSize}
              className={buttonClassName}
            >
              <MoreHorizontalIcon aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="start"
          container={portalContainer ?? undefined}
        >
          <DropdownMenuItem
            onClick={listState.collapseAllGroups}
            disabled={!listState.canCollapseMore}
          >
            {Localization.translator.collapseAll}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={listState.expandAllGroups}
            disabled={!listState.canExpandMore}
          >
            {Localization.translator.expandAll}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
